import React, { Component } from 'react';
import { Modal, Button, Alert, Form, Row, Col, Badge, Card, Spinner } from 'react-bootstrap';
import swal from 'sweetalert';
import { stockService } from '../../../services/StockService';

class StockPaymentModal extends Component {
    constructor(props) {
        super(props);

        this.state = {
            loading: true,
            processing: false,
            step: 'info', // 'info' or 'payment'

            // Payment data
            paymentInfo: null,
            eligibilityInfo: null,
            costCalculation: null,

            // Payment form
            paymentForm: {
                provider: 'OrangeMoney',
                phoneNumber: '',
                clientName: '',
                clientEmail: '',
                countryCode: 'SN',
                paymentReason: 'Achat de stock'
            },

            error: null
        };
    }

    componentDidMount() {
        if (this.props.isOpen && this.props.stockItem) {
            this.loadPaymentData();
        }
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.isOpen && this.props.isOpen && this.props.stockItem) {
            this.loadPaymentData();
        }
        if (prevProps.stockItem?.id !== this.props.stockItem?.id && this.props.stockItem) {
            this.loadPaymentData();
        }
    }

    loadPaymentData = async () => {
        const { stockItem } = this.props;
        if (!stockItem || !stockItem.id) return;

        this.setState({ loading: true, error: null });

        try {
            // Get payment info and eligibility
            const [paymentInfo, eligibilityInfo] = await Promise.all([
                stockService.getStockPaymentInfo(stockItem.id),
                stockService.checkStockPaymentEligibility(stockItem.id)
            ]);

            this.setState({
                paymentInfo,
                eligibilityInfo,
                paymentForm: {
                    ...this.state.paymentForm,
                    clientName: paymentInfo.productName || ''
                }
            });

        } catch (error) {
            console.error('Error loading payment data:', error);
            this.setState({ error: error.message || 'Erreur de chargement' });
        } finally {
            this.setState({ loading: false });
        }
    };

    calculateCost = async () => {
        const { stockItem } = this.props;

        try {
            this.setState({ processing: true, error: null });

            const costCalculation = await stockService.calculateStockPaymentCost(
                stockItem.id,
                'Standard'
            );

            this.setState({ costCalculation, step: 'payment' });

        } catch (error) {
            console.error('Error calculating cost:', error);
            this.setState({ error: error.message || 'Erreur de calcul' });
        } finally {
            this.setState({ processing: false });
        }
    };

    processPayment = async () => {
        const { stockItem } = this.props;
        const { paymentForm, costCalculation } = this.state;

        if (!paymentForm.phoneNumber && paymentForm.provider !== 'BankTransfer') {
            this.setState({ error: 'Numéro de téléphone requis' });
            return;
        }

        this.setState({ processing: true, error: null });

        try {
            const paymentData = {
                stockId: stockItem.id,
                provider: paymentForm.provider,
                amount: costCalculation.totalCost,
                currency: costCalculation.currency,
                phoneNumber: paymentForm.phoneNumber,
                clientName: paymentForm.clientName,
                clientEmail: paymentForm.clientEmail,
                countryCode: paymentForm.countryCode,
                paymentReason: paymentForm.paymentReason
            };

            const result = await stockService.processStockPayment(paymentData);

            if (result.success) {
                swal({
                    title: "Paiement réussi",
                    text: `Transaction ID: ${result.transactionId}`,
                    icon: "success",
                    button: "OK"
                });

                // Refresh data
                await this.loadPaymentData();
                this.setState({ step: 'info' });

                if (this.props.onPaymentSuccess) {
                    this.props.onPaymentSuccess(result);
                }
            } else {
                this.setState({ error: result.message });
            }

        } catch (error) {
            console.error('Payment error:', error);
            this.setState({ error: error.message || 'Erreur de paiement' });
        } finally {
            this.setState({ processing: false });
        }
    };

    getPaymentStatusBadge = (status) => {
        const variants = {
            'Pending': 'warning',
            'Completed': 'success',
            'Failed': 'danger',
            'Processing': 'info'
        };

        const labels = {
            'Pending': 'En attente',
            'Completed': 'Payé',
            'Failed': 'Échec',
            'Processing': 'En cours'
        };

        return (
            <Badge bg={variants[status] || 'secondary'}>
                {labels[status] || status}
            </Badge>
        );
    };

    renderInfoTab = () => {
        const { paymentInfo, eligibilityInfo } = this.state;
        const { stockItem } = this.props;

        if (!paymentInfo) return null;

        return (
            <div>
                {/* Stock Info */}
                <Card className="mb-3">
                    <Card.Body>
                        <h6 className="mb-3">Informations du stock</h6>
                        <Row>
                            <Col md={6}>
                                <p><strong>Code produit:</strong> {paymentInfo.productCode}</p>
                                <p><strong>Nom:</strong> {paymentInfo.productName}</p>
                            </Col>
                            <Col md={6}>
                                <p><strong>Quantité:</strong> {paymentInfo.quantity}</p>
                                <p><strong>Prix unitaire:</strong> {paymentInfo.unitPrice.toLocaleString()} XOF</p>
                            </Col>
                        </Row>
                        <hr />
                        <p className="mb-0">
                            <strong>Valeur totale:</strong> {' '}
                            <span className="h5 text-primary">
                                {paymentInfo.totalValue.toLocaleString()} XOF
                            </span>
                        </p>
                    </Card.Body>
                </Card>

                {/* Payment Status */}
                <Card className="mb-3">
                    <Card.Body>
                        <h6 className="mb-3">Statut de paiement</h6>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <p className="mb-1">
                                    <strong>Statut actuel:</strong> {' '}
                                    {this.getPaymentStatusBadge(paymentInfo.paymentStatus)}
                                </p>
                                {paymentInfo.lastTransactionId && (
                                    <small className="text-muted">
                                        Transaction: {paymentInfo.lastTransactionId}
                                    </small>
                                )}
                            </div>
                            {paymentInfo.lastPaymentDate && (
                                <div className="text-end">
                                    <small className="text-muted">
                                        Dernier paiement: {new Date(paymentInfo.lastPaymentDate).toLocaleDateString()}
                                    </small>
                                </div>
                            )}
                        </div>
                    </Card.Body>
                </Card>

                {/* Eligibility */}
                {eligibilityInfo && (
                    <Alert variant={eligibilityInfo.isEligible ? 'success' : 'warning'}>
                        <Alert.Heading>
                            <i className={`fa ${eligibilityInfo.isEligible ? 'fa-check-circle' : 'fa-exclamation-triangle'} me-2`} />
                            {eligibilityInfo.reason}
                        </Alert.Heading>
                        {eligibilityInfo.isEligible && (
                            <div>
                                <hr />
                                <p className="mb-0">
                                    <strong>Coût estimé:</strong> {' '}
                                    {eligibilityInfo.estimatedCost.toLocaleString()} {eligibilityInfo.currency}
                                </p>
                            </div>
                        )}
                    </Alert>
                )}

                {/* Payment History */}
                {paymentInfo.paymentHistory && paymentInfo.paymentHistory.length > 0 && (
                    <Card>
                        <Card.Body>
                            <h6 className="mb-3">Historique des paiements</h6>
                            <div className="table-responsive">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Transaction</th>
                                            <th>Montant</th>
                                            <th>Statut</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paymentInfo.paymentHistory.map((payment, idx) => (
                                            <tr key={idx}>
                                                <td>{new Date(payment.paymentDate).toLocaleDateString()}</td>
                                                <td><code>{payment.transactionId}</code></td>
                                                <td>{payment.amount.toLocaleString()} XOF</td>
                                                <td>{this.getPaymentStatusBadge(payment.status)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card.Body>
                    </Card>
                )}
            </div>
        );
    };

    renderPaymentTab = () => {
        const { costCalculation, paymentForm, processing } = this.state;

        return (
            <div>
                {/* Cost Calculation */}
                {costCalculation && (
                    <Card className="mb-3">
                        <Card.Body>
                            <h6 className="mb-3">Détail des coûts</h6>
                            <table className="table table-sm">
                                <tbody>
                                    <tr>
                                        <td>Sous-total:</td>
                                        <td className="text-end">{costCalculation.subTotal.toLocaleString()} XOF</td>
                                    </tr>
                                    <tr>
                                        <td>Frais de traitement (2%):</td>
                                        <td className="text-end">{costCalculation.processingFees.toLocaleString()} XOF</td>
                                    </tr>
                                    <tr>
                                        <td>TVA (18%):</td>
                                        <td className="text-end">{costCalculation.tax.toLocaleString()} XOF</td>
                                    </tr>
                                    <tr className="table-primary fw-bold">
                                        <td>Total à payer:</td>
                                        <td className="text-end">{costCalculation.totalCost.toLocaleString()} XOF</td>
                                    </tr>
                                </tbody>
                            </table>
                        </Card.Body>
                    </Card>
                )}

                {/* Payment Form */}
                <Card>
                    <Card.Body>
                        <h6 className="mb-3">Informations de paiement</h6>
                        <Form>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Méthode de paiement</Form.Label>
                                        <Form.Select
                                            value={paymentForm.provider}
                                            onChange={(e) => this.setState({
                                                paymentForm: { ...paymentForm, provider: e.target.value }
                                            })}
                                        >
                                            <option value="OrangeMoney">Orange Money</option>
                                            <option value="MtnMoney">MTN Money</option>
                                            <option value="BankTransfer">Virement Bancaire</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Numéro de téléphone</Form.Label>
                                        <Form.Control
                                            type="tel"
                                            value={paymentForm.phoneNumber}
                                            onChange={(e) => this.setState({
                                                paymentForm: { ...paymentForm, phoneNumber: e.target.value }
                                            })}
                                            placeholder="+221 77 123 45 67"
                                            disabled={paymentForm.provider === 'BankTransfer'}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nom du client</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={paymentForm.clientName}
                                            onChange={(e) => this.setState({
                                                paymentForm: { ...paymentForm, clientName: e.target.value }
                                            })}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Email</Form.Label>
                                        <Form.Control
                                            type="email"
                                            value={paymentForm.clientEmail}
                                            onChange={(e) => this.setState({
                                                paymentForm: { ...paymentForm, clientEmail: e.target.value }
                                            })}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-3">
                                <Form.Label>Motif du paiement</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={paymentForm.paymentReason}
                                    onChange={(e) => this.setState({
                                        paymentForm: { ...paymentForm, paymentReason: e.target.value }
                                    })}
                                />
                            </Form.Group>
                        </Form>
                    </Card.Body>
                </Card>
            </div>
        );
    };

    render() {
        const { isOpen, onClose, stockItem } = this.props;
        const { loading, processing, error, step, eligibilityInfo, costCalculation } = this.state;

        return (
            <Modal
                show={isOpen}
                onHide={onClose}
                size="lg"
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fa fa-credit-card me-2" />
                        Paiement du stock - {stockItem?.productName}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {error && (
                        <Alert variant="danger" dismissible onClose={() => this.setState({ error: null })}>
                            {error}
                        </Alert>
                    )}

                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </Spinner>
                        </div>
                    ) : (
                        <div>
                            {/* Navigation buttons */}
                            <div className="mb-3">
                                <Button
                                    variant={step === 'info' ? 'primary' : 'outline-primary'}
                                    size="sm"
                                    className="me-2"
                                    onClick={() => this.setState({ step: 'info' })}
                                >
                                    Informations
                                </Button>
                                <Button
                                    variant={step === 'payment' ? 'primary' : 'outline-primary'}
                                    size="sm"
                                    onClick={() => this.setState({ step: 'payment' })}
                                    disabled={!costCalculation}
                                >
                                    Paiement
                                </Button>
                            </div>

                            {/* Content */}
                            {step === 'info' ? this.renderInfoTab() : this.renderPaymentTab()}
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={onClose}>
                        Fermer
                    </Button>

                    {step === 'info' && eligibilityInfo?.isEligible && (
                        <Button
                            variant="primary"
                            onClick={this.calculateCost}
                            disabled={processing}
                        >
                            {processing ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Calcul en cours...
                                </>
                            ) : (
                                <>
                                    <i className="fa fa-calculator me-2" />
                                    Calculer le coût
                                </>
                            )}
                        </Button>
                    )}

                    {step === 'payment' && costCalculation && (
                        <Button
                            variant="success"
                            onClick={this.processPayment}
                            disabled={processing}
                        >
                            {processing ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Traitement...
                                </>
                            ) : (
                                <>
                                    <i className="fa fa-check me-2" />
                                    Confirmer le paiement
                                </>
                            )}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        );
    }
}

export default StockPaymentModal;